
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AnimatedProgress } from '@/components/ui/animated-progress';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Package } from 'lucide-react';
import { formatNumber } from '@/utils/format';

interface MaterialBreakdownItem {
  category: string;
  totalCost: number;
  percentage: number;
  items: number;
}

interface MaterialsBreakdownCardProps {
  data: MaterialBreakdownItem[];
  totalCost: number;
  className?: string;
  /**
   * Estimates già filtrati dalla dashboard: usati per calcolare al volo
   * i top materiali per categoria quando l'utente espande una riga.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  estimatesData?: any[];
}

const CATEGORY_LABEL: Record<string, string> = {
  board: 'Lastre',
  structure_frame: 'Montanti',
  structure_guide: 'Guide',
  insulation: 'Isolanti',
  accessory: 'Accessori',
  screw: 'Viti',
  other: 'Altri',
};

interface MaterialRollup {
  id: string;
  name: string;
  supplier: string | null;
  totalCost: number;
  totalQty: number;
  unit: string | null;
}

/**
 * Calcola al volo i top materiali per una categoria specifica dai preventivi
 * filtrati. Aggrega quantità (area × incidenza) e costo (qty × unit_price).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function aggregateMaterialsByCategory(estimates: any[], category: string): MaterialRollup[] {
  const map = new Map<string, MaterialRollup>();
  for (const e of estimates) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stratis = (e?.estimate_stratigraphies as any[] | null) ?? [];
    for (const s of stratis) {
      const area = Number(s?.area ?? 0);
      const layers = (s?.stratigraphy_data?.layers as unknown[] | undefined) ?? [];
      for (const l of layers) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mat = (l as any)?.materials;
        if (!mat || mat.category !== category) continue;
        const incidence = Number(mat.incidence_per_sqm ?? 1);
        const unitPrice = Number(mat.unit_price ?? 0);
        const qty = area * incidence;
        const cost = qty * unitPrice;
        const ex = map.get(mat.id) ?? {
          id: mat.id,
          name: mat.name ?? 'Senza nome',
          supplier: mat.supplier ?? null,
          totalCost: 0,
          totalQty: 0,
          unit: mat.unit ?? null,
        };
        ex.totalCost += cost;
        ex.totalQty += qty;
        map.set(mat.id, ex);
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => b.totalCost - a.totalCost);
}

export const MaterialsBreakdownCard: React.FC<MaterialsBreakdownCardProps> = ({
  data,
  totalCost,
  className,
  estimatesData = [],
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  const categoryColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
    'board': 'primary',
    'structure_frame': 'secondary',
    'structure_guide': 'secondary',
    'insulation': 'success',
    'accessory': 'warning',
    'screw': 'primary',
  };

  // Espansione: calcola i top materiali della categoria selezionata.
  const expandedMaterials = useMemo<MaterialRollup[] | null>(() => {
    if (!expanded) return null;
    return aggregateMaterialsByCategory(estimatesData, expanded).slice(0, 10);
  }, [expanded, estimatesData]);

  return (
    <Card className={`animate-fade-in ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          Breakdown Materiali
        </CardTitle>
        <CardDescription>
          Distribuzione costi per categoria · Totale {`€ ${Math.round(totalCost).toLocaleString('it-IT')}`} · Clicca una riga per vedere i top materiali
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {data.slice(0, 6).map((item, index) => {
          const isOpen = expanded === item.category;
          const label = CATEGORY_LABEL[item.category] ?? item.category.replace('_', ' ');
          return (
            <div key={item.category} className="space-y-1">
              <button
                onClick={() => setExpanded(isOpen ? null : item.category)}
                className={
                  'w-full text-left space-y-1.5 p-2 -mx-2 rounded transition-colors ' +
                  (isOpen ? 'bg-primary/5' : 'hover:bg-muted/50')
                }
                style={{ animationDelay: `${index * 100}ms` }}
                title="Clicca per espandere i materiali della categoria"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? (
                      <ChevronDown className="h-3.5 w-3.5 text-primary shrink-0" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    )}
                    <span className="font-medium capitalize">{label}</span>
                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                      {item.items} usi
                    </Badge>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold tabular-nums">€ {Math.round(item.totalCost).toLocaleString('it-IT')}</div>
                    <div className="text-[10px] text-muted-foreground">{formatNumber(item.percentage, 1)}%</div>
                  </div>
                </div>
                <AnimatedProgress
                  value={item.percentage}
                  max={100}
                  color={categoryColors[item.category] || 'primary'}
                  className="h-2"
                />
              </button>

              {isOpen && expandedMaterials && (
                <div className="ml-5 pl-3 border-l-2 border-primary/30 py-2 space-y-1.5">
                  {expandedMaterials.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">
                      Nessun materiale di questa categoria nei preventivi filtrati.
                    </p>
                  ) : (
                    <>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                        Top materiali — preventivi filtrati
                      </div>
                      {expandedMaterials.map((mat, mIdx) => (
                        <div
                          key={mat.id}
                          className="flex items-center justify-between gap-2 text-xs py-1 px-2 rounded hover:bg-muted/40"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-muted-foreground/70 tabular-nums w-4 text-right">{mIdx + 1}.</span>
                            <div className="min-w-0">
                              <div className="font-medium truncate">{mat.name}</div>
                              {mat.supplier && (
                                <div className="text-[10px] text-muted-foreground truncate">{mat.supplier}</div>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-mono font-semibold">€ {Math.round(mat.totalCost).toLocaleString('it-IT')}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {formatNumber(mat.totalQty, 1)}{mat.unit ? ` ${mat.unit}` : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
